import * as React from "react";
import { useModal } from "../../../../utils/custom-hooks";
import { IssueFileUpload } from "../../../organisms/smart-apply/modals/issue-file-upload";
import {
  ApplicationEntity,
  UploadIssueFile,
} from "../../../../application/domain/application/application-entity";
import { matchTotalPage } from "../../../helpers/match-total-page";
import { ResourceFetchError } from "../../../organisms/common/resource-fetch-error";
import {
  DangerButton,
  PrimaryButton,
  SecondaryButton,
} from "@graffer-inc/console-design-kit";
import { LgpkiVerifyResult } from "@graffer-inc/cloud-gov-core";
import apiClient from "../../../../application/apis/api-client";
import { LgpkiVerifyResultModal } from "../../../organisms/smart-apply/modals/lgpki-verify-result-modal";
import { toast } from "react-toastify";

interface AppDetailBasicInfoProps {
  application: ApplicationEntity;
  addFile: (issueFile: UploadIssueFile, skipNotification: boolean) => void;
  deleteFile: (index: number) => void;
  isLoading: boolean;
}

export const AppDetailIssueFile: React.FC<AppDetailBasicInfoProps> = ({
  application,
  addFile,
  deleteFile,
  isLoading,
}) => {
  const uploadModal = useModal(false);
  const verifyLgpkiModal = useModal();

  const [verifyResult, setVerifyResult] = React.useState<
    LgpkiVerifyResult | undefined
  >();
  const verifyLgpkiFile = async (applicationId: string, index: number) => {
    const { data: verified } = await apiClient.verifyLgpkiFile(
      applicationId,
      index,
    );
    setVerifyResult(verified);
  };

  return (
    <div className="columns">
      <div className="column is-8 has-text-left">
        <>
          {matchTotalPage(application.issueFiles.length, {
            NO_RESOURCE: (
              <ResourceFetchError
                faIcon="fa-folder-open"
                title="交付物はアップロードされていません"
                subtitle={
                  <>
                    {!application.isWip() ? (
                      <p>
                        対応ステータスが「処理中」でないため交付物をアップロードできません。
                      </p>
                    ) : (
                      <>
                        <p className="pb-5">
                          アップロードボタンより、交付物をアップロードしてください
                        </p>

                        <PrimaryButton
                          text={"アップロードする"}
                          disabled={!application.isWip()}
                          onClick={uploadModal.open}
                        />
                      </>
                    )}
                  </>
                }
              />
            ),
            RESOURCE_EXISTS: (
              <>
                {application.issueFiles.map((f, index) => {
                  return (
                    <React.Fragment key={index}>
                      <div className="box pt-5 pb-5">
                        <div className="columns is-desktop is-vcentered">
                          <div
                            className="column"
                            style={{
                              width: "70%",
                            }}
                          >
                            <div className="has-text-weight-bold is-size-5">
                              <p
                                style={{
                                  overflowWrap: "break-word",
                                }}
                              >
                                {f.name}
                              </p>
                              {f.isDownloaded && (
                                <p className="tags">
                                  <span className="tag is-medium">
                                    申請者ダウンロード済み
                                  </span>
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="column is-narrow">
                            {!f.isDeleted ? (
                              <span className="buttons is-right">
                                {f.isLgpkiSigned && (
                                  <SecondaryButton
                                    text={"署名を検証"}
                                    onClick={async () => {
                                      verifyLgpkiModal.open();
                                      try {
                                        await verifyLgpkiFile(
                                          application.id,
                                          index,
                                        );
                                      } catch (e) {
                                        toast.error("署名の検証に失敗しました");
                                        verifyLgpkiModal.close();
                                      }
                                    }}
                                  />
                                )}

                                <SecondaryButton
                                  text={"ダウンロード"}
                                  onClick={() => {
                                    window.open(
                                      application.formatIssueFileURLWithIndex(
                                        index,
                                      ),
                                      "_blank",
                                    );
                                  }}
                                />
                                <DangerButton
                                  text={"削除する"}
                                  disabled={application.isDone()}
                                  onClick={() => {
                                    deleteFile(index);
                                  }}
                                />
                              </span>
                            ) : (
                              <p className="tags is-right">
                                <span className="tag is-medium is-warning">
                                  交付キャンセル済み
                                </span>
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
                <SecondaryButton
                  text={"アップロードする"}
                  disabled={!application.isWip()}
                  onClick={uploadModal.open}
                />
              </>
            ),
          })}
        </>
      </div>
      {uploadModal.isActive && (
        <IssueFileUpload
          isActive={uploadModal.isActive}
          onUpload={addFile}
          onClose={uploadModal.close}
          isLoading={isLoading}
        />
      )}
      {verifyLgpkiModal.isActive && (
        // 表示する情報量が多くなったり、
        // 特定の申請ではなく任意のファイルを検証できるようにした際には
        // ダイアログではなく専用のページを設けることを検討
        <LgpkiVerifyResultModal
          result={verifyResult}
          isActive={verifyLgpkiModal.isActive}
          onClose={() => {
            verifyLgpkiModal.close();
            setVerifyResult(undefined);
          }}
        />
      )}
    </div>
  );
};
